<template>
  <div class="spp-billing-page">
    <!-- 顶部操作栏 -->
    <div class="spp-toolbar">
      <div class="toolbar-left">
        <el-select v-model="selectedMonth" placeholder="选择月份" style="width: 140px" @change="loadFiles">
          <el-option v-for="m in monthOptions" :key="m" :label="formatMonth(m)" :value="m" />
        </el-select>
        <el-select v-model="selectedCustomer" placeholder="选择客户" clearable style="width: 180px" @change="filterDepartments">
          <el-option v-for="c in customerList" :key="c" :label="c" :value="c" />
        </el-select>
        <el-button type="primary" @click="generateBills" :loading="generating" :disabled="!selectedMonth">
          生成对账单
        </el-button>
      </div>
      <div class="toolbar-right">
        <el-tooltip content="按 AWS 项目标签(tag)拆分账单前，需先从 CUR 数据解析各 link 的 tag 金额。仅配置了 Tag 拆分的部门需要。" placement="bottom">
          <el-button @click="refreshTags" :loading="refreshingTags">同步标签账单数据</el-button>
        </el-tooltip>
        <el-button @click="showRateDialog = true">汇率设置</el-button>
        <el-button @click="showDeptDialog = true">部门管理</el-button>
      </div>
    </div>

    <!-- 当月汇率提示 -->
    <div class="rate-info" v-if="currentRate">
      <span>{{ selectedMonth }} 月汇率：1 USD = {{ currentRate }} CNY</span>
    </div>
    <div class="rate-info rate-warning" v-else-if="selectedMonth">
      <span>⚠️ {{ selectedMonth }} 月汇率未设置</span>
      <el-button type="primary" link @click="showRateDialog = true">去设置</el-button>
    </div>

    <!-- 部门列表 + 生成状态 -->
    <div class="spp-content">
      <el-table :data="filteredDepartments" stripe style="width: 100%">
        <el-table-column prop="customer_name" label="客户" min-width="120" />
        <el-table-column prop="department_name" label="部门" min-width="160" />
        <el-table-column prop="link_count" label="Link数" width="70" align="center" />
        <el-table-column label="返点" width="70" align="center">
          <template #default="{ row }">{{ (row.discount_rate * 100).toFixed(0) }}%</template>
        </el-table-column>
        <el-table-column prop="currency" label="币种" width="70" align="center" />
        <el-table-column label="模板" min-width="180">
          <template #default="{ row }">
            <span class="template-path">{{ row.template_file || '未设置' }}</span>
          </template>
        </el-table-column>
        <el-table-column label="本月账单" min-width="240">
          <template #default="{ row }">
            <span v-if="getFileForDept(row)" class="file-generated">
              ✓ 对账明细
              <el-button type="primary" link size="small" @click="downloadFile(getFileForDept(row))">下载明细</el-button>
            </span>
            <span v-else class="file-pending">未生成</span>
            <el-button type="primary" link size="small" @click="downloadDeptPackage(row)">打包下载(含原始)</el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- 已生成文件列表 -->
    <div class="spp-files" v-if="generatedFiles.length > 0">
      <h4>已生成文件（{{ selectedMonth }}月）</h4>
      <div class="file-list">
        <div v-for="f in generatedFiles" :key="f.filename" class="file-item">
          <span class="file-name">{{ f.filename }}</span>
          <span class="file-meta">{{ f.size > 1024 ? (f.size/1024).toFixed(1) + 'KB' : f.size + 'B' }} · {{ f.mtime }}</span>
        </div>
      </div>
    </div>

    <!-- 汇率设置弹窗 -->
    <el-dialog v-model="showRateDialog" title="汇率设置" width="400px">
      <el-form label-width="100px">
        <el-form-item label="月份">
          <el-input v-model="rateForm.month" placeholder="如 202604" />
        </el-form-item>
        <el-form-item label="USD→CNY">
          <el-input v-model="rateForm.rate" placeholder="如 7.5178" />
        </el-form-item>
      </el-form>
      <div class="rate-history" v-if="rateHistory.length">
        <h5>历史汇率</h5>
        <div v-for="r in rateHistory" :key="r.month" class="rate-row">
          <span>{{ formatMonth(r.month) }}</span>
          <span>{{ r.rate }}</span>
        </div>
      </div>
      <template #footer>
        <el-button @click="showRateDialog = false">取消</el-button>
        <el-button type="primary" @click="saveRate">保存</el-button>
      </template>
    </el-dialog>

    <!-- 部门管理弹窗 -->
    <el-dialog v-model="showDeptDialog" title="部门管理" width="900px" top="5vh">
      <div class="dept-manage">
        <div style="margin-bottom: 12px; text-align: right;">
          <el-button type="primary" size="small" @click="openDeptForm()">新增部门</el-button>
        </div>
        <el-table :data="departments" stripe max-height="420">
          <el-table-column prop="customer_name" label="客户" width="110" />
          <el-table-column prop="department_name" label="部门" width="150" />
          <el-table-column label="返点" width="60" align="center">
            <template #default="{ row }">{{ (row.discount_rate * 100).toFixed(0) }}%</template>
          </el-table-column>
          <el-table-column prop="currency" label="币种" width="55" />
          <el-table-column label="Tag拆分" width="90">
            <template #default="{ row }">{{ row.split_by_tag || '-' }}</template>
          </el-table-column>
          <el-table-column label="Links" min-width="180">
            <template #default="{ row }">
              <span class="dept-links-text">{{ (row.links || []).map(l => l.link_id).join(', ') }}</span>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="120">
            <template #default="{ row }">
              <el-button type="primary" link size="small" @click="openDeptForm(row)">编辑</el-button>
              <el-button type="danger" link size="small" @click="deleteDept(row)">删除</el-button>
            </template>
          </el-table-column>
        </el-table>
      </div>
      <template #footer>
        <el-button @click="showDeptDialog = false">关闭</el-button>
      </template>
    </el-dialog>

    <!-- 部门编辑表单弹窗 -->
    <el-dialog v-model="showDeptFormDialog" :title="deptForm.id ? '编辑部门' : '新增部门'" width="760px" top="5vh">
      <el-form :model="deptForm" label-width="100px" size="small">
        <el-row :gutter="12">
          <el-col :span="12">
            <el-form-item label="客户名称" required>
              <el-input v-model="deptForm.customer_name" placeholder="如 阳光电源" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="部门名称" required>
              <el-input v-model="deptForm.department_name" placeholder="如 上海暨算" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="12">
          <el-col :span="8">
            <el-form-item label="返点率">
              <el-input v-model.number="deptForm.discount_rate" placeholder="0.15 = 15%" />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="币种">
              <el-select v-model="deptForm.currency" style="width: 100%">
                <el-option label="CNY" value="CNY" />
                <el-option label="USD" value="USD" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="语言">
              <el-select v-model="deptForm.lang" style="width: 100%">
                <el-option label="中文" value="cn" />
                <el-option label="英文" value="en" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="12">
          <el-col :span="8">
            <el-form-item label="底部类型">
              <el-select v-model="deptForm.footer_type" style="width: 100%">
                <el-option label="国内(税号/开户行)" value="cn_invoice" />
                <el-option label="海外(收付款抬头)" value="hk_payment" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="Tag拆分">
              <el-input v-model="deptForm.split_by_tag" placeholder="如 user_project，空=不拆" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="模板文件" required>
          <el-input v-model="deptForm.template_file" placeholder="如 阳光模板/阳光电源股份有限公司.xlsx" />
        </el-form-item>

        <el-divider content-position="left">Link 映射</el-divider>
        <div style="margin-bottom: 8px;">
          <el-button type="primary" size="small" plain @click="addDeptLink">添加 Link</el-button>
        </div>
        <el-table :data="deptForm.links" size="small" max-height="240" border>
          <el-table-column label="Link ID" width="160">
            <template #default="{ row }">{{ row.link_id }}</template>
          </el-table-column>
          <el-table-column label="Customer Name">
            <template #default="{ row }"><el-input v-model="row.customer_display" size="small" /></template>
          </el-table-column>
          <el-table-column label="End User">
            <template #default="{ row }"><el-input v-model="row.end_user" size="small" /></template>
          </el-table-column>
          <el-table-column label="排序" width="70">
            <template #default="{ row }"><el-input v-model.number="row.sort_order" size="small" /></template>
          </el-table-column>
          <el-table-column label="操作" width="60">
            <template #default="{ $index }">
              <el-button type="danger" link size="small" @click="deptForm.links.splice($index, 1)">删除</el-button>
            </template>
          </el-table-column>
        </el-table>
      </el-form>
      <template #footer>
        <el-button @click="showDeptFormDialog = false">取消</el-button>
        <el-button type="primary" @click="saveDept" :loading="savingDept">保存</el-button>
      </template>
    </el-dialog>

    <!-- 添加 Link 弹窗 -->
    <el-dialog v-model="showAddLinkDialog" title="添加 Link" width="480px" append-to-body>
      <el-form :model="addLinkForm" label-width="120px" size="default">
        <el-form-item label="Link" required>
          <el-select v-model="addLinkForm.link_id" filterable clearable placeholder="搜索选择 Link"
            :filter-method="linkFilterMethod" @change="onAddLinkSelect" style="width: 100%">
            <el-option v-for="item in displayedLinksList" :key="item.link_id"
              :label="(item.customer_name || item.name || item.link_id) + '（' + item.link_id + '）'"
              :value="item.link_id" />
          </el-select>
        </el-form-item>
        <el-form-item label="Customer Name">
          <el-input v-model="addLinkForm.customer_display" placeholder="对账单 Customer Name 列" />
        </el-form-item>
        <el-form-item label="End User">
          <el-input v-model="addLinkForm.end_user" placeholder="对账单 End User 列" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="showAddLinkDialog = false">取消</el-button>
        <el-button type="primary" @click="confirmAddLink">确定</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ajaxGet, ajaxPost, ajaxPut, ajaxDelete1, download } from '@/api/request'
import { getLinkerList } from '@/api/api'

export default {
  name: 'sppBilling',
  data() {
    return {
      selectedMonth: '',
      selectedCustomer: '',
      departments: [],
      customerList: [],
      generatedFiles: [],
      generating: false,
      refreshingTags: false,
      currentRate: null,
      // link 下拉数据源
      linksList: [],
      displayedLinksList: [],
      // 添加 Link 弹窗
      showAddLinkDialog: false,
      addLinkForm: { link_id: '', customer_display: '', end_user: '' },
      // 汇率
      showRateDialog: false,
      rateForm: { month: '', rate: '' },
      rateHistory: [],
      // 部门管理
      showDeptDialog: false,
      showDeptFormDialog: false,
      savingDept: false,
      deptForm: {
        id: null,
        customer_name: '',
        department_name: '',
        discount_rate: 0,
        currency: 'CNY',
        lang: 'cn',
        footer_type: 'cn_invoice',
        split_by_tag: '',
        template_file: '',
        links: [],
      },
    }
  },
  computed: {
    monthOptions() {
      const now = new Date()
      const months = []
      for (let i = 0; i < 6; i++) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
        months.push(d.getFullYear() * 100 + (d.getMonth() + 1))
      }
      return months
    },
    filteredDepartments() {
      if (!this.selectedCustomer) return this.departments
      return this.departments.filter(d => d.customer_name === this.selectedCustomer)
    }
  },
  mounted() {
    // 默认选上一月（如 5 月出的是 4 月账单）
    this.selectedMonth = this.monthOptions[1] || this.monthOptions[0]
    this.loadDepartments()
    this.loadRates()
    this.loadFiles()
    this.getLinksList()
  },
  methods: {
    formatMonth(m) {
      return `${Math.floor(m / 100)}-${String(m % 100).padStart(2, '0')}`
    },
    async loadDepartments() {
      const res = await ajaxGet({ url: 'bill/spp/departments/' })
      if (res.code === 2000) {
        this.departments = res.data.departments || []
        this.customerList = res.data.customer_list || []
      }
    },
    async loadRates() {
      const res = await ajaxGet({ url: 'bill/spp/exchange-rate/' })
      if (res.code === 2000) {
        this.rateHistory = res.data || []
        this.updateCurrentRate()
      }
    },
    async loadFiles() {
      if (!this.selectedMonth) return
      this.updateCurrentRate()
      const res = await ajaxGet({ url: 'bill/spp/files/', params: { month: this.selectedMonth } })
      if (res.code === 2000) {
        this.generatedFiles = res.data.files || []
      }
    },
    updateCurrentRate() {
      const found = this.rateHistory.find(r => r.month === this.selectedMonth)
      this.currentRate = found ? found.rate : null
      this.rateForm.month = String(this.selectedMonth)
    },
    filterDepartments() {
      // computed handles filtering
    },
    async generateBills() {
      if (!this.selectedMonth) return
      if (!this.currentRate) {
        this.$message.warning('请先设置当月汇率')
        this.showRateDialog = true
        return
      }
      this.generating = true
      try {
        const ids = this.filteredDepartments.map(d => d.id)
        const res = await ajaxPost({
          url: 'bill/spp/generate/',
          params: {
            month: this.selectedMonth,
            department_ids: ids.length === this.departments.length ? 'all' : ids,
          }
        })
        if (res.code === 2000) {
          const data = res.data
          this.$message.success(`生成完成：成功 ${data.total} 个，失败 ${data.failed} 个`)
          this.loadFiles()
        } else {
          this.$message.error(res.msg || '生成失败')
        }
      } catch (e) {
        this.$message.error('请求失败: ' + e.message)
      } finally {
        this.generating = false
      }
    },
    async refreshTags() {
      if (!this.selectedMonth) {
        this.$message.warning('请先选择月份')
        return
      }
      this.refreshingTags = true
      try {
        const res = await ajaxPost({ url: 'bill/spp/tag-refresh/', params: { month: this.selectedMonth } })
        if (res.code === 2000) {
          const d = res.data
          let msg = `Tag刷新完成：解析 ${d.parsed_count} 个 link`
          if (d.download_errors && d.download_errors.length) {
            msg += `，下载失败 ${d.download_errors.length}（可能未投递）`
          }
          this.$message.success(msg)
        } else {
          this.$message.error(res.msg || '刷新失败')
        }
      } catch (e) {
        this.$message.error('请求失败: ' + e.message)
      } finally {
        this.refreshingTags = false
      }
    },
    async saveRate() {
      if (!this.rateForm.month || !this.rateForm.rate) {
        this.$message.warning('请填写月份和汇率')
        return
      }
      const res = await ajaxPost({ url: 'bill/spp/exchange-rate/', params: this.rateForm })
      if (res.code === 2000) {
        this.$message.success(res.msg)
        this.showRateDialog = false
        this.loadRates()
      } else {
        this.$message.error(res.msg || '保存失败')
      }
    },
    getFileForDept(dept) {
      const safeName = dept.department_name.replace(/[/\\]/g, '_')
      return this.generatedFiles.find(f => f.filename.includes(safeName))
    },
    // 后端出错时返回 JSON（HTTP 200），若直接存成文件会得到无法打开的损坏 zip/xlsx。
    // 此处检测 blob 是否为 JSON 错误，是则弹出后端 msg 并返回 true。
    async checkBlobError(blob) {
      const isJson = blob && ((blob.type && blob.type.indexOf('json') !== -1) || blob.type === '')
      if (!isJson) return false
      try {
        const text = await blob.text()
        const obj = JSON.parse(text)
        if (obj && (obj.code !== undefined || obj.msg)) {
          this.$message.error(obj.msg || '下载失败')
          return true
        }
      } catch (e) {
        // 解析失败说明不是 JSON（可能是二进制文件），按正常文件处理
      }
      return false
    },
    saveBlob(blob, filename) {
      const url = window.URL.createObjectURL(new Blob([blob]))
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)
    },
    async downloadFile(file) {
      try {
        const blob = await download('bill/spp/download/', {
          month: this.selectedMonth,
          filename: file.filename,
        })
        if (await this.checkBlobError(blob)) return
        this.saveBlob(blob, file.filename)
      } catch (e) {
        this.$message.error('下载失败: ' + (e.message || e))
      }
    },
    async downloadDeptPackage(dept) {
      try {
        const blob = await download('bill/spp/dept-package/', {
          month: this.selectedMonth,
          department_id: dept.id,
        })
        if (await this.checkBlobError(blob)) return
        const fname = `SPP-${dept.customer_name}-${dept.department_name}-${this.selectedMonth}.zip`
        this.saveBlob(blob, fname)
      } catch (e) {
        this.$message.error('打包下载失败: ' + (e.message || e))
      }
    },
    openDeptForm(row) {
      if (row) {
        this.deptForm = {
          id: row.id,
          customer_name: row.customer_name,
          department_name: row.department_name,
          discount_rate: row.discount_rate,
          currency: row.currency,
          lang: row.lang || 'cn',
          footer_type: row.footer_type || 'cn_invoice',
          split_by_tag: row.split_by_tag || '',
          template_file: row.template_file || '',
          links: (row.links || []).map(l => ({
            link_id: l.link_id,
            customer_display: l.customer_display || '',
            end_user: l.end_user || '',
            sort_order: l.sort_order || 0,
          })),
        }
      } else {
        this.deptForm = {
          id: null, customer_name: '', department_name: '', discount_rate: 0,
          currency: 'CNY', lang: 'cn', footer_type: 'cn_invoice', split_by_tag: '',
          template_file: '', links: [],
        }
      }
      this.showDeptFormDialog = true
    },
    addDeptLink() {
      this.addLinkForm = { link_id: '', customer_display: '', end_user: '' }
      this.displayedLinksList = this.linksList.slice(0, 50)
      this.showAddLinkDialog = true
    },
    onAddLinkSelect(linkId) {
      const item = this.linksList.find(l => String(l.link_id) === String(linkId))
      if (item) {
        this.addLinkForm.customer_display = item.customer_name || item.company || ''
        this.addLinkForm.end_user = item.name || item.customer_name || ''
      }
    },
    confirmAddLink() {
      if (!this.addLinkForm.link_id) {
        this.$message.warning('请选择 Link')
        return
      }
      if (this.deptForm.links.some(l => String(l.link_id) === String(this.addLinkForm.link_id))) {
        this.$message.warning('该 Link 已存在')
        return
      }
      const links = this.deptForm.links.slice()
      links.push({
        link_id: this.addLinkForm.link_id,
        customer_display: this.addLinkForm.customer_display || '',
        end_user: this.addLinkForm.end_user || '',
        sort_order: links.length + 1,
      })
      this.deptForm.links = links
      this.showAddLinkDialog = false
    },
    getLinksList() {
      getLinkerList({ page: 1, limit: 9999 }).then(res => {
        if (res && res.code === 2000) {
          this.linksList = res.data.data || res.data || []
          this.displayedLinksList = this.linksList.slice(0, 50)
        }
      }).catch(() => {})
    },
    linkFilterMethod(query) {
      if (!query) {
        this.displayedLinksList = this.linksList.slice(0, 50)
        return
      }
      const q = query.trim()
      this.displayedLinksList = this.linksList.filter(item =>
        String(item.link_id).includes(q) ||
        (item.customer_name && item.customer_name.includes(q)) ||
        (item.name && item.name.includes(q))
      ).slice(0, 50)
    },
    async saveDept() {
      if (!this.deptForm.customer_name || !this.deptForm.department_name || !this.deptForm.template_file) {
        this.$message.warning('请填写客户名称、部门名称、模板文件')
        return
      }
      this.savingDept = true
      try {
        const fn = this.deptForm.id ? ajaxPut : ajaxPost
        const res = await fn({ url: 'bill/spp/departments/', params: this.deptForm })
        if (res.code === 2000) {
          this.$message.success(res.msg || '保存成功')
          this.showDeptFormDialog = false
          this.loadDepartments()
        } else {
          this.$message.error(res.msg || '保存失败')
        }
      } catch (e) {
        this.$message.error('请求失败: ' + e.message)
      } finally {
        this.savingDept = false
      }
    },
    deleteDept(row) {
      this.$confirm(`确定删除部门「${row.department_name}」？`, '提示', { type: 'warning' })
        .then(async () => {
          const res = await ajaxDelete1({ url: 'bill/spp/departments/', params: { id: row.id } })
          if (res.code === 2000) {
            this.$message.success('删除成功')
            this.loadDepartments()
          } else {
            this.$message.error(res.msg || '删除失败')
          }
        })
        .catch(() => {})
    },
  }
}
</script>

<style scoped>
.spp-billing-page {
  padding: 20px;
  background: #f0f2f5;
  min-height: calc(100vh - 120px);
}

.spp-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding: 16px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06);
}

.toolbar-left {
  display: flex;
  gap: 12px;
  align-items: center;
}

.toolbar-right {
  display: flex;
  gap: 8px;
}

.rate-info {
  padding: 8px 16px;
  margin-bottom: 12px;
  background: #f0f9ff;
  border-radius: 6px;
  font-size: 13px;
  color: #0369a1;
}

.rate-warning {
  background: #fffbeb;
  color: #b45309;
}

.spp-content {
  background: #fff;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06);
}

.template-path {
  font-size: 12px;
  color: #71717a;
  font-family: ui-monospace, monospace;
}

.file-generated {
  color: #16a34a;
  font-size: 12px;
}

.file-pending {
  color: #a1a1aa;
  font-size: 12px;
}

.spp-files {
  margin-top: 16px;
  background: #fff;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06);
}

.spp-files h4 {
  margin: 0 0 12px;
  font-size: 14px;
  color: #18181b;
}

.file-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.file-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  border-bottom: 1px solid #f0f0f0;
}

.file-item:last-child {
  border-bottom: none;
}

.file-name {
  flex: 1;
  font-size: 13px;
  color: #18181b;
}

.file-meta {
  flex-shrink: 0;
  text-align: right;
  font-size: 12px;
  color: #71717a;
}

.rate-history {
  margin-top: 16px;
  border-top: 1px solid #e4e4e7;
  padding-top: 12px;
}

.rate-history h5 {
  margin: 0 0 8px;
  font-size: 13px;
  color: #71717a;
}

.rate-row {
  display: flex;
  justify-content: space-between;
  padding: 4px 0;
  font-size: 13px;
}

.dept-manage {
  max-height: 60vh;
  overflow: auto;
}
</style>
